import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadStayReviewRequestApi, submitStayReviewApi, type StayReviewRequestDto } from "../../api/rateAdApi";
import styles from "./RateStay.module.css";

const RateStayPage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();
  const [request, setRequest] = useState<StayReviewRequestDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await loadStayReviewRequestApi(token);
        if (!cancelled) setRequest(response?.data?.data || null);
      } catch (err: any) {
        if (!cancelled) {
          const status = Number(err?.response?.status || 0);
          setError(status === 409 ? t("rateStay.alreadySubmitted") : status === 410 ? t("rateStay.expired") : t("rateStay.loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [t, token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = comment.trim();
    if (!content) {
      setError(t("rateStay.commentRequired"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await submitStayReviewApi(token, { rating, comment: content.charAt(0).toUpperCase() + content.slice(1) });
      setSubmitted(true);
    } catch (err: any) {
      const status = Number(err?.response?.status || 0);
      setError(status === 409 ? t("rateStay.alreadySubmitted") : status === 410 ? t("rateStay.expired") : t("rateStay.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        {loading ? (
          <p className={styles.state}>{t("rateStay.loading")}</p>
        ) : submitted ? (
          <>
            <h1>{t("rateStay.successTitle")}</h1>
            <p className={styles.success}>{t("rateStay.successMessage")}</p>
            {request?.adId ? <Link className={styles.adTitle} to={`/ads/${request.adId}`}>{request.adTitle}</Link> : null}
          </>
        ) : error && !request ? (
          <>
            <h1>{t("rateStay.unavailableTitle")}</h1>
            <p className={styles.error}>{error}</p>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h1>{t("rateStay.title")}</h1>
            <p>{t("rateStay.subtitle", { name: request?.clientName || t("rateStay.clientFallback") })}</p>
            {request?.adId ? <Link className={styles.adTitle} to={`/ads/${request.adId}`}>{request.adTitle}</Link> : null}
            <div className={styles.stars} aria-label={t("rateStay.ratingLabel")}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`${styles.starButton} ${star <= rating ? styles.starButtonActive : ""}`}
                  onClick={() => setRating(star)}
                  aria-label={t("rateStay.rateStar", { count: star })}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              className={styles.textarea}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              maxLength={2000}
              placeholder={t("rateStay.commentPlaceholder")}
              disabled={submitting}
            />
            {error ? <p className={styles.error}>{error}</p> : null}
            <div className={styles.actions}>
              <button type="submit" className={styles.submit} disabled={submitting}>
                {submitting ? t("rateStay.submitting") : t("rateStay.submit")}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
};

export default RateStayPage;
